import React, { useEffect, useState } from 'react';
import { API_BASE } from '../config';
import AddressModal from './AddressModal';

export default function DownloadsPage() {
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingSlug, setDownloadingSlug] = useState(null);
  const [signatureStatus, setSignatureStatus] = useState({ checked: false, signedAt: null, address: null, addressComplete: false });
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showSignatureCta, setShowSignatureCta] = useState(false);

  useEffect(() => {
    const checkSig = async () => {
      try {
        const res = await fetch(`${API_BASE}/signature/status`, { credentials: 'include' });
        const data = await res.json();
        if (res.ok) setSignatureStatus({ checked: true, signedAt: data.signedAt, address: data.address, addressComplete: !!data.addressComplete });
        else setSignatureStatus({ checked: true, signedAt: null, address: null, addressComplete: false });
      } catch (_) {
        setSignatureStatus({ checked: true, signedAt: null, address: null, addressComplete: false });
      }
    };
    checkSig();
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/repos`, { credentials: 'include' });
        const data = await res.json();
        if (res.ok) setRepos(data.data || []);
        else {
          if (data && data.error === 'signature_or_address_missing') {
            // Always fetch latest signature/address status to decide which reason to display
            try {
              const sres = await fetch(`${API_BASE}/signature/status`, { credentials: 'include' });
              const sdata = await sres.json();
              const addrOk = !!sdata.addressComplete;
              const signed = !!sdata.signedAt;
              if (!signed) {
                setError('Please sign the digital delivery agreement to access downloads.');
                setShowSignatureCta(true);
              } else if (!addrOk) {
                setError('Your billing address is incomplete. Please fill street, postal code, and country.');
                setShowAddressModal(true);
              } else {
                setError('Access is restricted until your address and signature are completed.');
              }
            } catch (_) {
              setError('Access is restricted until your address and signature are completed.');
            }
          } else {
            setError(data.error || 'Failed to load');
          }
        }
      } catch (e) {
        setError('Network error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const downloadArchive = async (slug) => {
    setDownloadingSlug(slug);
    try {
      const res = await fetch(`${API_BASE}/repos/${slug}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ type: 'archive' })
      });
      const data = await res.json();
      if (res.ok && data.token) {
        window.location.href = `${API_BASE}/download?token=${encodeURIComponent(data.token)}`;
        // Intentionally do not reset downloadingSlug here to keep spinner visible until navigation/download starts
      } else {
        if (data && data.error === 'signature_or_address_missing') {
          if (!signatureStatus.signedAt) {
            setError('Please sign the digital delivery agreement to access downloads.');
            setShowSignatureCta(true);
          } else if (!signatureStatus.addressComplete) {
            setError('Your billing address is incomplete. Please fill street, postal code, and country.');
            setShowAddressModal(true);
          }
        } else {
          setError(data && data.error ? data.error : 'Failed to start download');
        }
        setDownloadingSlug(null);
      }
    } catch (e) {
      setDownloadingSlug(null);
    }
  };

  if (loading) return <div>Loading…</div>;
  if (error) return <div style={{ color: '#a33' }}>{error} {(error.includes('signature') || error.includes('agreement')) && <a style={{ marginLeft: 10 }} href="/signature" onClick={(e)=>{e.preventDefault(); window.history.pushState({}, '', '/signature'); window.dispatchEvent(new PopStateEvent('popstate'));}}>Go to signature</a>}</div>;

  if (signatureStatus.checked && (!signatureStatus.signedAt || !signatureStatus.addressComplete)) {
    if (!signatureStatus.signedAt) {
      return (
        <div style={{ marginTop: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>Signature required</h2>
          <p>
            Please sign the digital delivery agreement to access downloads.
          </p>
          {/* 
            This is a button-styled link that, when clicked, navigates the user to the "/signature" page
            without causing a full page reload. It uses the History API to update the URL and dispatches
            a PopStateEvent so that the React app's router can handle the navigation.
          */}
          <a
            className="btn"
            href="/signature"
            onClick={(e) => {
              e.preventDefault();
              window.history.pushState({}, '', '/signature');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
          >
            Go to signature
          </a>
        </div>
      );
    }
    // Address incomplete case
    return (
      <div style={{ marginTop: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>Billing address required</h2>
        <p>Your billing address is incomplete. Please fill street, postal code, and country.</p>
        <AddressModal open={showAddressModal || !signatureStatus.addressComplete} onClose={()=>setShowAddressModal(false)} onSaved={()=>{ setSignatureStatus((s)=>({ ...s, addressComplete: true })); }} />
      </div>
    );
  }

  return (
    <div style={{ marginTop: 20 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>Downloads</h2>
      {!repos.length && <div>No repositories available. If you recently donated, try again later.</div>}
      {error && (
        <div style={{ color: '#a33', marginBottom: 8 }}>
          {error}
          {(showSignatureCta || error.includes('signature') || error.includes('agreement')) && (
            <>
              {' '}
              <a href="/signature" onClick={(e)=>{e.preventDefault(); window.history.pushState({}, '', '/signature'); window.dispatchEvent(new PopStateEvent('popstate'));}}>Go to signature</a>
            </>
          )}
        </div>
      )}
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {repos.map((r) => (
          <li key={r.slug} style={{ border: '1px solid #d0d7de', background: '#f6f8fa', borderRadius: 6, padding: 12, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 600 }}>{r.name}</div>
              <div>
                <button
                  className="btn"
                  onClick={() => downloadArchive(r.slug)}
                  disabled={downloadingSlug === r.slug}
                  aria-busy={downloadingSlug === r.slug}
                >
                  {downloadingSlug === r.slug ? (
                    <>
                      <span className="spinner" /> Preparing…
                    </>
                  ) : (
                    'Download .zip'
                  )}
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
      <AddressModal open={showAddressModal} onClose={()=>setShowAddressModal(false)} onSaved={()=>{ setSignatureStatus((s)=>({ ...s, addressComplete: true })); setShowAddressModal(false); }} />
    </div>
  );
}


